'use client';

import { Calendar as CalendarIcon } from 'lucide-react';
import * as React from 'react';
import { DateRange } from 'react-day-picker';

import { cn } from '@ui/lib/utils';

import { Button } from '../../../common/button';
import { ResponsivePopover } from '../../../common/responsivePopover';
import { Checkbox } from '../../../ui/checkbox';
import { DateRangeDialog } from './date-range-dialog';

const defaultIcon = <CalendarIcon className="w-5 h-5 text-neutral-900" />;
const defaultValue: string[] = [];

interface DateRangeOption {
  value: string;
  label: string;
}

interface DateRangeFilterProps {
  label: string;
  icon?: React.ReactNode;
  options: DateRangeOption[];
  value: string[];
  customDateRange?: DateRange;
  onChange: (value: string[], customRange?: DateRange) => void;
  className?: string;
  customDateLabel?: string;
  clearText?: string;
  showClear?: boolean;
  /**
   * Maximum number of days the custom range can span. Defaults to 90 for
   * search use cases where querying a larger rolling window would be
   * expensive; pass `undefined` to remove the cap entirely.
   */
  maxDays?: number;
  locale?: string;
  translations?: {
    cancel?: string;
    apply?: string;
    maxDaysWarning?: string;
  };
  /**
   * When true, the label/text is hidden on small screens (< sm) and only the
   * icon is shown. The label remains visible on sm and larger breakpoints.
   */
  hideLabelOnMobile?: boolean;
}

export const DateRangeFilter = ({
  label,
  icon = defaultIcon,
  options,
  value = defaultValue,
  customDateRange,
  onChange,
  className,
  customDateLabel = 'Custom Duration',
  clearText = 'Clear',
  showClear = false,
  maxDays = 90,
  locale = 'en',
  translations,
  hideLabelOnMobile = false
}: DateRangeFilterProps) => {
  const [open, setOpen] = React.useState(false);
  const [showDatePicker, setShowDatePicker] = React.useState(false);

  const handleOptionToggle = (optionValue: string, checked: boolean) => {
    if (optionValue === 'custom') {
      if (checked) {
        // Open date picker dialog
        setShowDatePicker(true);
      } else {
        // Remove custom from selection and clear date range
        const newValue = value.filter((v) => v !== 'custom');
        onChange(newValue, undefined);
      }
    } else {
      // Handle regular option toggle
      const newValue = checked ? [...value, optionValue] : value.filter((v) => v !== optionValue);
      onChange(newValue, customDateRange);
    }
  };

  const handleDateRangeConfirm = (dateRange: DateRange) => {
    const newValue = value.includes('custom') ? value : [...value, 'custom'];
    onChange(newValue, dateRange);
  };

  // Determine display text based on selections
  let displayText: string;

  if (value.length === 0) {
    displayText = label;
  } else if (value.includes('custom') && customDateRange?.from && customDateRange?.to) {
    // Format custom date range
    const fromDateRaw = customDateRange.from.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
    const toDateRaw = customDateRange.to.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
    // Capitalize first letter for display (French months are lowercase)
    const fromDate = fromDateRaw.charAt(0).toUpperCase() + fromDateRaw.slice(1);
    const toDate = toDateRaw.charAt(0).toUpperCase() + toDateRaw.slice(1);
    displayText = `${fromDate} - ${toDate}`;
  } else {
    // Show selected preset options
    const selectedOptions = options.filter((option) => value.includes(option.value));
    if (selectedOptions.length === 1) {
      displayText = selectedOptions[0].label;
    } else if (selectedOptions.length > 1) {
      displayText = `${selectedOptions.length} selected`;
    } else {
      displayText = label;
    }
  }

  // Check if any values are selected
  const isSelected = value.length > 0;

  const triggerButton = (
    <button
      type="button"
      className={cn(
        'flex items-center flex-row gap-2 px-3 py-2 border rounded-lg transition-colors cursor-pointer min-w-0',
        hideLabelOnMobile ? 'justify-center sm:justify-start' : '',
        isSelected
          ? 'bg-primary-0 border-primary-200 hover:border-primary-300 [&_svg]:!text-primary-500'
          : 'bg-white border-neutral-200 hover:border-neutral-300',
        className
      )}
    >
      <span className={cn('flex-shrink-0 flex items-center', isSelected ? 'text-primary-500' : '')}>{icon}</span>
      <span
        className={cn(
          'text-sm font-normal whitespace-nowrap truncate max-w-[200px]',
          hideLabelOnMobile ? 'hidden sm:inline' : '',
          isSelected ? 'text-primary-500' : 'text-neutral-900'
        )}
      >
        {displayText}
      </span>
    </button>
  );

  return (
    <>
      <ResponsivePopover
        open={open}
        onOpenChange={setOpen}
        trigger={triggerButton}
        title={label}
        contentClassName="w-[200px] p-2"
      >
        <div className="space-y-2">
          {options.map((option) => (
            <label
              key={option.value}
              className="flex items-center space-x-2 cursor-pointer hover:bg-neutral-100 p-2 rounded"
            >
              <Checkbox
                checked={value.includes(option.value)}
                onCheckedChange={(checked: boolean) => handleOptionToggle(option.value, checked)}
              />
              <span className="text-sm font-normal text-neutral-900">{option.label}</span>
            </label>
          ))}
          {showClear && (
            <div className="mt-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  onChange([], undefined);
                  setOpen(false);
                }}
                className="w-full"
                label={clearText}
                disabled={value.length === 0}
              />
            </div>
          )}
        </div>
      </ResponsivePopover>

      <DateRangeDialog
        open={showDatePicker}
        onOpenChange={setShowDatePicker}
        initialDateRange={customDateRange}
        onConfirm={handleDateRangeConfirm}
        title={customDateLabel}
        maxDays={maxDays}
        locale={locale}
        translations={translations}
      />
    </>
  );
};
